import * as yup from 'yup';

import httpClient from 'httpClient';
import drupalIcon from 'static/drupal_icon.png';
import eosIcon from 'static/eos_icon.png';
import gpsIcon from 'static/gitlab_icon.png';
import nexusIcon from 'static/nexus_icon.png';
import paasIcon from 'static/paas_icon.png';
import wordpressIcon from 'static/wordpress_icon.png';
import wordpressIconDark from 'static/wordpress_icon_dark.png';

import {isDev} from './environmentUtils';

const categories = [
  {
    value: 'Official',
    label: 'Official - reassigned when owner leaves',
    purpose: 'Official - recommended for projects and teams',
  },
  {
    value: 'Personal',
    label: 'Personal - deleted when owner leaves',
    purpose: 'Personal - recommended for personal sites',
  },
  {
    value: 'Test',
    label: 'Test - deleted 6 months after creation',
    purpose: 'Test - recommended for testing',
  },
];

export const categoryOptions = categories.map(({value, label}) => ({
  value,
  text: label,
}));

export const wordpressCategoryOptions = categories
  .filter(c => c.value === 'Test' || c.value === 'Official')
  .map(({value, label}) => ({
    value,
    text: label,
  }));

export const categoryPurposes = categories.map(({purpose}) => purpose);

export const wordpressCategoryPurposes = categories
  .filter(c => c.value === 'Test' || c.value === 'Official')
  .map(({purpose}) => purpose);

export const siteVisibilityOptions = [
  {
    value: 'Intranet',
    text: 'Intranet - Access only possible within CERN Intranet',
  },
  {
    value: 'Internet',
    text: 'Internet- Enable access from outside CERN',
  },
];

export const sitesTypeInfo = {
  'webeos': {
    icon: eosIcon,
    info: 'All about WebEOS site configuration',
    documentationUrl: 'https://webeos.docs.cern.ch',
  },
  'drupal': {
    icon: drupalIcon,
    info: 'All about Drupal site configuration',
    documentationUrl: 'https://drupal.docs.cern.ch',
  },
  'paas': {
    icon: paasIcon,
    info: 'All about PaaS project configuration',
    documentationUrl: 'https://paas.docs.cern.ch',
  },
  'app-catalogue': {
    icon: paasIcon,
    info: 'All about App Catalogue project \nconfiguration',
    documentationUrl: 'https://paas.docs.cern.ch/9._Application-Templates/',
  },
  'nexus': {
    icon: nexusIcon,
    info: 'All about Nexus instance \nconfiguration',
    documentationUrl: 'https://paas.docs.cern.ch/9._Application-Templates/',
  },
  'gps': {
    icon: gpsIcon,
    info: 'All about GitLab Pages site configuration',
    documentationUrl: 'https://how-to.docs.cern.ch',
  },
  'Wordpress': {
    icon: wordpressIcon,
    info: 'All about WordPress (Legacy) site configuration',
    documentationUrl: 'https://wordpress.docs.cern.ch',
  },
  'wordpress': {
    icon: wordpressIconDark,
    info: 'All about WordPress site configuration',
    documentationUrl: 'https://wordpress.docs.cern.ch',
  },
};

export const subdomainOptions = ['.web.cern.ch', '.docs.cern.ch', '.webtest.cern.ch'];

export function buildSubdomainDropdownOptions() {
  return subdomainOptions.map(opt => ({
    text: opt,
    key: opt,
    value: opt,
  }));
}

export const drupalSubdomain = '.web.cern.ch';
export const wordpressSubdomain = isDev ? '.webtest.cern.ch' : '.web.cern.ch';

export const siteMinLength = 4;
export const drupalMinLength = 2;
export const wordpressMinLength = 2; // to be looked up later

export const validationSchemas = {
  categoryValidation: yup
    .string()
    .required("Site's category is required")
    .oneOf(
      categoryOptions.map(opt => opt.value),
      "Category must be one of 'Official', 'Personal', 'Test'"
    ),
  descriptionValidation: yup
    .string()
    .required('Please enter a meaningful description for the site'),
  nameValidation: yup
    .string()
    .required("Site's name is required")
    .matches(
      /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
      "Site name must consist of lowercase alphanumeric characters or '-', and must start and end with an alphanumeric character"
    )
    .min(siteMinLength, `Minimum allowed length of a site name is ${siteMinLength} characters`)
    .max(63, 'Maximum allowed length of a site name is 63 characters'),
  updateNameValidation: yup
    .string()
    .required("Site's name is required")
    .matches(
      /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
      "Site name must consist of lowercase alphanumeric characters or '-', and must start and end with an alphanumeric character"
    )
    .max(63, 'Maximum allowed length of a site name is 63 characters'),
  drupalNameValidation: yup
    .string()
    .required("Site's name is required")
    .matches(
      /^[a-z]([-a-z0-9]*[a-z0-9])?$/,
      "Site name must consist of lowercase alphanumeric characters or '-', and must start with an alphabetic character and end with an alphanumeric character"
    )
    .min(drupalMinLength, `Minimum allowed length of a site name is ${drupalMinLength} characters`)
    .max(50, 'Maximum allowed length of a site name is 50 characters'),
  // This validation is required to allow changing/adding aliases/main URL after creation:
  // https://gitlab.cern.ch/webservices/webservices-portal/-/merge_requests/132#note_5256287
  drupalSiteURLValidation: yup
    .string()
    .required("Site's URL is required")
    .matches(
      /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
      "Site URL must consist of lowercase alphanumeric characters or '-', and must start and end with an alphanumeric character"
    )
    .min(drupalMinLength, `Minimum allowed length of a site URL is ${drupalMinLength} characters`)
    .max(50, 'Maximum allowed length of a site URL is 50 characters'),
  wordpressSiteURLValidation: yup
    .string()
    .required("Site's URL is required")
    .matches(
      /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/,
      "Site URL must consist of lowercase alphanumeric characters or '-', and must start and end with an alphanumeric character"
    )
    .min(
      wordpressMinLength,
      `Minimum allowed length of a site URL is ${wordpressMinLength} characters`
    )
    .max(50, 'Maximum allowed length of a site URL is 50 characters'),
  subdomainValidation: yup
    .string()
    .required("Site's subdomain is required")
    .oneOf(
      subdomainOptions.map(opt => opt),
      "Subdomain must be either '.web.cern.ch' or '.docs.cern.ch'"
    ),
  eosPathValidation: yup
    .string()
    .required("Site's path is required")
    .matches(
      /^\/eos(\/[^/]+){2,}\/?$/,
      `EOS path must have at least two non-empty path segments after initial /eos, e.g. '/eos/user/j/john/www'`
    ),
  computingRulesApproved: yup.boolean().oneOf([true]),
  siteVisibilityValidation: yup
    .string()
    .required("Site's visibility is required")
    .oneOf(
      siteVisibilityOptions.map(opt => opt.value),
      "Visibility must be either 'Internet' or 'Intranet'"
    ),
};

// supported versions to be discussed
export const formatWordPressVersionsOptions = supportedWordPressVersions =>
  supportedWordPressVersions.map(version => ({key: version, text: version, value: version}));

export const formatDrupalVersionsOptions = supportedDrupalVersions =>
  supportedDrupalVersions.map(version => ({key: version, text: version, value: version}));

export const formatWebEOSServerVersionsOptions = supportedWebEOSServerVersions => {
  if (!supportedWebEOSServerVersions) {
    return [];
  }

  const versionToText = {cc7: 'CERN CentOS 7', el9: 'AlmaLinux 9'};
  return supportedWebEOSServerVersions.map(version => ({
    key: version,
    text: version in versionToText ? versionToText[version] : version,
    value: version,
  }));
};

export const validateEosPath = (eosPath, projectCategory, currentOwnerInfo) => {
  if (!eosPath) {
    return true;
  }

  const eosPathOfficial = /^\/eos\/(user|project)-([a-z])\/(.*)/;
  const regexMatch = eosPath.match(eosPathOfficial);

  if (regexMatch) {
    const suggestedEosPath = `/eos/${regexMatch[1]}/${regexMatch[2]}/${regexMatch[3]}`;
    throw new Error(
      `Only paths in a canonical form are allowed: Use "${suggestedEosPath}" rather than "${eosPath}"`
    );
  }

  if (projectCategory === 'Official') {
    if (eosPath !== undefined) {
      const eosPathOfficial =
        /^\/eos\/(project|atlas|ams|cms|experiment|lhcb|media|theory|workspace|web)\//;
      const eosPathOfficialService = new RegExp(
        `^/eos/(project|atlas|ams|cms|experiment|lhcb|media|theory|workspace|web|user/${currentOwnerInfo.username.charAt(
          0
        )}/${currentOwnerInfo.username})/`
      );
      if (currentOwnerInfo.type === 'Service' && eosPath.match(eosPathOfficialService) === null) {
        throw new Error(`For a site with category Official owned by a Service account, EOS path must start with one of:
            '/eos/user/${currentOwnerInfo.username.charAt(0)}/${
              currentOwnerInfo.username
            }/', /eos/project/', '/eos/atlas/', '/eos/ams/', '/eos/cms/', '/eos/experiment/',
          '/eos/lhcb/', '/eos/media/', '/eos/theory/', '/eos/workspace/'. E.g. '/eos/project/my-project/www'`);
      } else if (currentOwnerInfo.type === 'Primary' && eosPath.match(eosPathOfficial) === null) {
        throw new Error(`For a site with category Official, EOS path must start with one of:
            '/eos/project/', '/eos/atlas/', '/eos/ams/', '/eos/cms/', '/eos/experiment/',
          '/eos/lhcb/', '/eos/media/', '/eos/theory/', '/eos/workspace/'. E.g. '/eos/project/my-project/www'`);
      }
    }
  } else if (projectCategory === 'Personal' || projectCategory === 'Test') {
    const eosPathOfficial = /^\/eos\/user\//;
    if (eosPath && eosPath.match(eosPathOfficial) === null) {
      throw new Error(`For a site with category Personal or Test, EOS path must start with:
            '/eos/user/' . E.g. '/eos/user/j/johnny/www'`);
    }
  }
  return true;
};

export const eosPathPlaceholder = {
  Official: 'Path to site content on EOS folder, e.g. /eos/project/www',
  Personal: 'Path to site content on EOS folder, e.g. /eos/user/j/john/www',
  Test: 'Path to site content on EOS folder, e.g. /eos/user/j/john/www',
};

export const siteSuccessMessage = (projectName, siteUrlChanged) => {
  return siteUrlChanged
    ? `The site ${projectName} was updated. The new URL will be reachable in a few seconds. Please note that it may take some time for the old URL(s) to be available for reuse again.`
    : `The site ${projectName} was updated.`;
};

export const buildAdminGroupUrl = adminGroup => {
  return `https://e-groups.cern.ch/e-groups/Egroup.do?egroupName=${adminGroup}`;
};

export const buildUserUrl = user => {
  return `https://account.cern.ch/account/CERNAccount/AccountStatus.aspx?login=${user}`;
};

export async function updateMatomoRegistration(siteId, siteUrl) {
  if (siteId === undefined || siteId === null) {
    return;
  }

  try {
    await httpClient.patch(
      `/api/web-analytics/registration`,
      {
        siteId,
        siteUrl,
      },
      {handleErrorsCentrally: false}
    );
  } catch (err) {
    let updateRegistrationErrorMessage = '';
    if (err.response.data.errors) {
      updateRegistrationErrorMessage += JSON.stringify(err.response.data.errors.json);
    }
    if (err.response.data.error) {
      updateRegistrationErrorMessage += err.response.data.error;
    }
    throw new Error(updateRegistrationErrorMessage);
  }
}

export function isWordPress(projectType) {
  return projectType === 'wordpress';
}
